import { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { useNWC } from '@/hooks/useNWCContext';
import { useStreamingPreferences } from '@/hooks/useStreamingPreferences';
import { Header } from '@/components/Header';
import { WalletModal } from '@/components/WalletModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Wallet,
  ArrowLeft,
  Zap,
  Music2,
  Heart,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export function WalletPage() {
  const { connections, activeConnection, getActiveConnection } = useNWC();
  const { 
    preferences, 
    updateArtistSats, 
    updateAppSats,
    toggleEnabled,
  } = useStreamingPreferences();
  
  const [balance, setBalance] = useState<number | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  useSeoMeta({
    title: 'Wallet & Settings - Cove',
    description: 'Manage your wallet and streaming payment settings',
  });

  const activeConn = getActiveConnection();
  const hasWallet = connections.length > 0 && connections.some(c => c.isConnected);

  // Fetch balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!hasWallet || !activeConn) return;
      
      setIsLoadingBalance(true);
      try {
        // Try to get balance using webln or NWC
        if (typeof window !== 'undefined' && 'webln' in window) {
          const webln = (window as unknown as { webln: { getBalance: () => Promise<{ balance: number }> } }).webln;
          try {
            await webln.enable?.();
            const info = await webln.getBalance();
            setBalance(info.balance);
          } catch {
            setBalance(null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch balance:', error);
        setBalance(null);
      } finally {
        setIsLoadingBalance(false);
      }
    };

    if (hasWallet && balance === null) {
      fetchBalance();
    }
  }, [hasWallet, activeConn, balance]);

  const formatSats = (sats: number) => {
    return new Intl.NumberFormat().format(sats);
  };

  const totalPerMinute = preferences.artistSatsPerMinute + preferences.appSatsPerMinute;

  return (
    <div className="min-h-screen bg-background pb-32">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Back Button */}
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold">Wallet & Settings</h1>
            <p className="text-muted-foreground">
              Configure your streaming payments and wallet connection
            </p>
          </div>

          {/* Wallet Connection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Lightning Wallet
              </CardTitle>
              <CardDescription>
                Connect a wallet to enable value-for-value streaming
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasWallet ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                      <div>
                        <p className="font-medium">Wallet Connected</p>
                        <p className="text-sm text-muted-foreground">
                          {activeConn?.alias || 'NWC Wallet'}
                        </p>
                      </div>
                    </div>
                    <WalletModal>
                      <Button variant="outline" size="sm">
                        Manage
                      </Button>
                    </WalletModal>
                  </div>

                  {/* Balance */}
                  {isLoadingBalance ? (
                    <Skeleton className="h-16 w-full" />
                  ) : balance !== null ? (
                    <div className="p-4 rounded-lg bg-muted/50 text-center">
                      <p className="text-sm text-muted-foreground mb-1">Balance</p>
                      <p className="text-2xl font-bold">
                        <Zap className="h-5 w-5 inline mr-1 text-primary" />
                        {formatSats(balance)} sats
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Wallet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">
                    No wallet connected
                  </p>
                  <WalletModal>
                    <Button>
                      <Wallet className="h-4 w-4 mr-2" />
                      Connect Wallet
                    </Button>
                  </WalletModal>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Streaming Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Streaming Payments
              </CardTitle>
              <CardDescription>
                Configure how much to pay artists while listening
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Streaming Payments</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically pay artists as you listen
                  </p>
                </div>
                <Switch
                  checked={preferences.enabled}
                  onCheckedChange={toggleEnabled}
                  disabled={!hasWallet}
                />
              </div>

              <Separator />

              {/* Artist Payment */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Music2 className="h-4 w-4 text-primary" />
                  <Label>Artist Payment</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Sats per minute paid directly to the artist
                </p>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[preferences.artistSatsPerMinute]}
                    onValueChange={(v) => updateArtistSats(v[0])}
                    min={0}
                    max={100}
                    step={1}
                    className="flex-1"
                    disabled={!hasWallet}
                  />
                  <div className="w-20">
                    <Input
                      type="number"
                      value={preferences.artistSatsPerMinute}
                      onChange={(e) => updateArtistSats(parseInt(e.target.value) || 0)}
                      min={0}
                      max={1000}
                      className="text-center"
                      disabled={!hasWallet}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {preferences.artistSatsPerMinute === 0 
                    ? 'Artists will not receive streaming payments'
                    : `Artists receive ${preferences.artistSatsPerMinute} sats/min`}
                </p>
              </div>

              <Separator />

              {/* App Support */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-pink-500" />
                  <Label>Support Cove</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Optional: Help support Cove development
                </p>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[preferences.appSatsPerMinute]}
                    onValueChange={(v) => updateAppSats(v[0])}
                    min={0}
                    max={21}
                    step={1}
                    className="flex-1"
                    disabled={!hasWallet}
                  />
                  <div className="w-20">
                    <Input
                      type="number"
                      value={preferences.appSatsPerMinute}
                      onChange={(e) => updateAppSats(parseInt(e.target.value) || 0)}
                      min={0}
                      max={100}
                      className="text-center"
                      disabled={!hasWallet}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {preferences.appSatsPerMinute === 0 
                    ? 'No app support (that\'s okay!)'
                    : `${preferences.appSatsPerMinute} sats/min to Cove`}
                </p>
              </div>

              <Separator />

              {/* Summary */}
              <div className={cn(
                'p-4 rounded-lg',
                preferences.enabled && totalPerMinute > 0 
                  ? 'bg-primary/10 border border-primary/20' 
                  : 'bg-muted/50'
              )}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total per minute</span>
                  <span className="text-xl font-bold">
                    <Zap className="h-4 w-4 inline mr-1 text-primary" />
                    {totalPerMinute} sats
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {!hasWallet ? (
                    'Connect a wallet to enable payments'
                  ) : !preferences.enabled ? (
                    'Streaming payments are disabled'
                  ) : totalPerMinute === 0 ? (
                    'Set a value above to support artists'
                  ) : (
                    `${preferences.artistSatsPerMinute} to artist + ${preferences.appSatsPerMinute} to Cove`
                  )}
                </p>
              </div>

              {!hasWallet && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-amber-700 dark:text-amber-300">
                    Connect a Lightning wallet above to enable streaming payments
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
